import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, MapPin, FileCheck, ShieldCheck, MonitorSmartphone, ArrowRight } from 'lucide-react';
const pillars = [{
  icon: TrendingUp,
  title: 'Growth & Revenue Strategy',
  desc: 'Client acquisition, retention, pricing, lane strategy, and repeatable growth planning.'
}, {
  icon: Users,
  title: 'Driver Recruiting & Retention',
  desc: 'Build a recruiting pipeline, tighten onboarding, improve communication, and reduce churn.'
}, {
  icon: MapPin,
  title: 'Dispatch & Load Management',
  desc: 'Move from improvising load-to-load to planned weekly movement, stronger rates, and better utilization.'
}, {
  icon: FileCheck,
  title: 'Back Office & Order-to-Cash',
  desc: 'Reduce paperwork, billing errors, reconciliation delays, and manual follow-up.'
}, {
  icon: ShieldCheck,
  title: 'Compliance & Risk',
  desc: 'Create cleaner processes around safety, documentation, accountability, and operational discipline.'
}, {
  icon: MonitorSmartphone,
  title: 'Technology & Visibility',
  desc: 'Clean up fragmented tools, improve reporting, automate what can be automated, and give leadership better visibility.'
}];
export function WhatWeInstall() {
  return <section id="services" className="section-padding bg-navy text-white relative overflow-hidden" data-id="element-349">
      {/* Industrial Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #ffffff 75%, #ffffff), linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #ffffff 75%, #ffffff)`,
      backgroundSize: '20px 20px',
      backgroundPosition: '0 0, 10px 10px'
    }} data-id="element-350"></div>

      <div className="container-custom relative z-10" data-id="element-351">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-352">
          <span className="eyebrow text-amber" data-id="element-353">What FleetScale Installs</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight" data-id="element-354">
            The Operating System Your Trucking Business Needs to Scale
          </h2>
          <p className="text-lg text-white/70 leading-relaxed" data-id="element-355">
            FleetScale is not a report that sits in a folder. The engagement is
            built around diagnosing what is broken, designing the roadmap, and
            helping your team implement the operating rhythm.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-center" data-id="element-356">
          {/* Left: Process Diagram Overlay */}
          <div className="lg:col-span-4 order-2 lg:order-1" data-id="element-357">
            <div className="bg-black/50 border border-white/10 p-8 rounded-sm backdrop-blur-sm relative" data-id="element-358">
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red via-amber to-navy" data-id="element-359"></div>
              <h4 className="font-heading font-bold text-white mb-8 uppercase tracking-widest text-sm flex items-center" data-id="element-360">
                <span className="w-2 h-2 bg-red rounded-full mr-3 animate-pulse" data-id="element-361"></span>
                Implementation Flow
              </h4>
              <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/20 before:to-transparent hidden md:block" data-id="element-362">
                {[{
                step: '1',
                title: 'Diagnose Bottlenecks',
                active: false
              }, {
                step: '2',
                title: 'Design Roadmap',
                active: false
              }, {
                step: '3',
                title: 'Install Operating Rhythm',
                active: true
              }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-363">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-black bg-navy shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_0_2px_rgba(255,255,255,0.1)] z-10" data-id="element-364">
                      <span className={`font-bold text-sm ${item.active ? 'text-red' : 'text-white/50'}`} data-id="element-365">
                        {item.step}
                      </span>
                    </div>
                    <div className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded border ${item.active ? 'bg-white/10 border-red/50 shadow-[0_0_15px_rgba(227,40,54,0.2)]' : 'bg-white/5 border-white/10'}`} data-id="element-366">
                      <div className={`font-bold text-sm ${item.active ? 'text-white' : 'text-white/70'}`} data-id="element-367">
                        {item.title}
                      </div>
                    </div>
                  </div>)}
              </div>

              {/* Mobile simplified flow */}
              <div className="md:hidden space-y-6 pl-4 border-l-2 border-white/10" data-id="element-368">
                {[{
                step: '1',
                title: 'Diagnose Bottlenecks',
                active: false
              }, {
                step: '2',
                title: 'Design Roadmap',
                active: false
              }, {
                step: '3',
                title: 'Install Operating Rhythm',
                active: true
              }].map((item, i) => <div key={i} className="relative" data-id="element-369">
                    <div className={`absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full border-2 border-black ${item.active ? 'bg-red' : 'bg-white/30'}`} data-id="element-370"></div>
                    <div className={`font-bold text-sm ${item.active ? 'text-white' : 'text-white/70'}`} data-id="element-371">
                      {item.step}. {item.title}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right: The 6 Pillars */}
          <div className="lg:col-span-8 order-1 lg:order-2" data-id="element-372">
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-373">
              {pillars.map((pillar, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="bg-white/5 border border-white/10 p-6 rounded-sm hover:bg-white/10 hover:border-white/30 transition-all group" data-id="element-374">
                  <div className="flex items-start mb-4" data-id="element-375">
                    <div className="w-12 h-12 bg-black/50 border border-white/10 flex items-center justify-center rounded-sm shrink-0 mr-4 group-hover:border-amber/50 transition-colors" data-id="element-376">
                      <pillar.icon className="w-6 h-6 text-amber" data-id="element-377" />
                    </div>
                    <h3 className="text-lg font-bold font-heading text-white pt-1" data-id="element-378">
                      {pillar.title}
                    </h3>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed pl-16" data-id="element-379">
                    {pillar.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>

        <div className="mt-16 text-center" data-id="element-380">
          <a href="#contact" className="btn-primary group" data-id="element-381">
            Build My Operating System
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-382" />
          </a>
        </div>
      </div>
    </section>;
}